import _ from 'lodash';
import { Model } from 'backbone';
import validate from 'validate.js';
import { leagueRules } from '../../validation_rules';
import Radio from 'backbone.radio';
import { SaveMultipartFormData } from '../../utils';

import User from '../User';
import { Player } from '../index';

const leagueChannel = Radio.channel('league');
const leagues_avatars_path = process.env.LEAGUE_AVATARS_PATH;
const saveMultipartFormData = new SaveMultipartFormData();

const FINAL_ROUNDS = {
	8: 4,
	6: 3,
	4: 2,
	2: 1,
	0: 0
};

const FINAL_ROUNDS_LABELS = {
	8: [
		'Qualifying/Elimination Final',
		'Semi Final',
		'Preliminary Final',
		'Grand Final'
	],
	6: [
		'Elimination Final',
		'Preliminary Final',
		'Grand Final'
	],
	4: [
		'Preliminary Final',
		'Grand Final'
	],
	2: [
		'Grand Final'
	]
};

/**
 * This is League Model Class
 */
export default class LeagueModel extends Model {
	/**
	 * Set default model fields
	 * @returns object
	 */
	get defaults() {
		return {
			id:                   0,
			name:                 '',
			avatar:               '',
			privacy:              0,
			type:                 'head_to_head',
			size:                 16,
			start_round:          1,
			bye_rounds:           0,
			finals_format:        8,
			play_times:           'flexible',
			coaches_box:          1,
			advanced_options:     'basic',
			rounds:               {},
			errors:               {},
			status:               'scheduled',
			code:                 '',
			first_name:           '',
			last_name:            '',
			size_list:            [ 4, 6, 8, 10, 12, 14, 16, 18, 20 ],
			final_format_size:    {
				0: 'No',
				2: 'Top 2',
				4: 'Top 4',
				6: 'Top 6',
				8: 'Top 8'
			},
			finals_start_round_enabled: true,
			finals_start_rounds: [],
			finals_start_round: 0,
			default_email_fields: _.range( 0, 5 ),
			offset:               0,
			num_teams:            0,
			teams:                {},
			is_club:              false,
			is_possible_leave:    false,
			is_leave_commissioner: false,
			display_avatar:       '',
			origin_type:          '',
			users:                [],
			started:              false,
			if_settings_access:   false,
			my_team:              {},
			fixture:              {},
			current_fixture:      {},
			current_round:        0,
			opponent:             false,
			team_rank:            '-',
			order:                'rank',
			order_direction:      'ASC',
			end_round:            0,
			finals:               0,
			round:                '',
			selected_round:       '',
			winner:               false,
			display_rounds:       {}
		};

	}

	/**
	 * Parse BE queries
	 * @param resp
	 * @returns {*}
	 */
	parse(resp) {
		let result = resp.result || resp;

		if (result === true) {
			this.trigger('league:invites:success');
		}

		if (_.isUndefined(result.id)) {
			this.trigger('league:query:not:found');
		}
		else {
			leagueChannel.trigger('league:query:response', result);
		}

		return result;
	}

	/**
	 * Validate current league data
	 * @returns {LeagueModel}
	 */
	validateLeague() {
		let errors = validate(
			this.toJSON(),
			leagueRules
		);
		this.set('errors', !_.isUndefined(errors) ? errors : {});

		this.trigger('league:form:errors', errors);

		return this;
	}

	isFinalRound( round ) {
		let finals_format = this.get( 'finals_format' ),
			end_round = this.get( 'end_round' ),
			final_rounds = FINAL_ROUNDS[finals_format],
			final_round = ( end_round - final_rounds ) + 1;
		return final_round <= round;
	}

	isFinalRounds( round, bye_rounds_arr ) {
		let finals_format = this.get( 'finals_format' ),
			end_round = this.get( 'end_round' ),
			bye_rounds = this.get('bye_rounds') ? 0 : bye_rounds_arr.length,
			final_rounds = FINAL_ROUNDS[finals_format],
			final_round = end_round - final_rounds - bye_rounds;
		return final_round < round;
	}

	/**
	 * Get create league url ( leagues_classic/create )
	 * @returns {string}
	 */
	getCreateLeagueUrl() {
		return 'leagues_classic/create';
	}

	/**
	 * Create new league
	 * @returns {LeagueModel}
	 */
	createLeague(formData) {
		const url = this.getCreateLeagueUrl();
		const data = {
			url,
			formData: formData
		};

		saveMultipartFormData
			.save(data)
			.then(response => {
				if (response.errors.length) {
					this.trigger('league:create:errors', response.errors);
				}
				else {
					this.set(response.result);
					this.trigger('league:create:success', response);
				}
			});

		return this;
	}

	/**
	 * Get update league url ( leagues_classic/update )
	 * @returns {string}
	 */
	getUpdateLeagueUrl() {
		return 'leagues_classic/update';
	}

	/**
	 * Update league by id
	 * @returns {LeagueModel}
	 */
	updateLeague() {
		let formData = this.getUpdateFormData(),
			data = {
				url: this.getUpdateLeagueUrl(),
				formData
			};

		saveMultipartFormData.save(data).then(response => {
			if (response.errors.length) {
				this.trigger('league:update:errors', response.errors);
			}
			else {
				this.set(response.result);
				this.trigger('league:update:success', response);
			}
		});

		return this;
	}

	/**
	 * Set leave url ( leagues_classic/leave )
	 * @returns {LeagueModel}
	 */
	setLeaveUrl() {
		this.url = 'leagues_classic/leave';
		return this;
	}

	/**
	 * Leave from the current league ( query to BE )
	 * @returns {LeagueModel}
	 */
	leave() {
		let data = {
			data: {
				league_id: this.get('id')
			},
			type: 'league:leave'
		};

		this.setLeaveUrl()
			.getData(data);

		return this;
	}

	/**
	 * Set url for getting one league
	 * @returns {LeagueModel}
	 */
	setShowUrl() {
		this.url = 'leagues_classic/show';
		return this;
	}

	/**
	 * Set url for getting one regenerate league
	 * @returns {LeagueModel}
	 */
	setRegenerateShowUrl() {
		this.url = 'leagues_classic/regenerate_show';
		return this;
	}

	/**
	 * Return field for current league
	 * @returns object
	 */
	getFormData() {
		let all_data = this.toJSON(),
			form_data = {
				name: all_data.name,
				avatar: all_data.avatar,
				privacy: all_data.privacy,
				type: all_data.type,
				size: all_data.size,
				start_round: all_data.start_round
			};

		if (all_data.type==='head_to_head') {
			form_data.bye_rounds = all_data.bye_rounds;
			form_data.finals_format = all_data.finals_format;
			form_data.play_times = all_data.play_times;
			if (this.get('finals_start_round_enabled')) {
				form_data.finals_start_round = all_data.finals_start_round;
			}
		}

		if (all_data.advanced_options==='advanced') {
			form_data.coaches_box = all_data.coaches_box;
		}

		return form_data;
	}

	/**
	 * Return field for update current league
	 * @returns object
	 */
	getUpdateFormData() {
		let
			status = this.get('status'),
			all_data = this.toJSON(),
			form_data = {
				id: all_data.id,
				name: all_data.name,
				avatar: all_data.avatar
			};

		if (status==='scheduled') {
			form_data.privacy = all_data.privacy;
			form_data.type = all_data.type;
			form_data.coaches_box = all_data.coaches_box;
			form_data.start_round = all_data.start_round;

			if (all_data.type==='head_to_head') {
				form_data.size = all_data.size;
				form_data.bye_rounds = all_data.bye_rounds;
				form_data.finals_format = all_data.finals_format;
				form_data.finals_start_round = all_data.finals_start_round;
				form_data.play_times = all_data.play_times;
			}
		}
		if (all_data.type==='head_to_head' 
		&& all_data.finals_format 
		&& all_data.finals_start_round) {
			form_data.finals_format = all_data.finals_format;
			form_data.finals_start_round = all_data.finals_start_round;
		}

		return form_data;

	}

	/**
	 * Getter to check if start round is over final round.
	 * @returns {boolean}
	 */
	get isLeagueFinal() {
		const
			start_round = parseInt(this.get('start_round'), 10),
			last_possible_final_round = 16;

		return start_round && start_round >= last_possible_final_round;
	}

	/**
	 * Getter to check if bye rounds is on in league
	 * @returns {boolean}
	 */
	get isOnByeRounds() {
		let bye_rounds = this.get('bye_rounds');
		return Number(bye_rounds) === 1;
	}

	get is_final_on() {
		return this.get('finals');
	}

	/**
	 * If start round === 16 - disabling finals_format field
	 * @returns {LeagueModel}
	 */
	recalculateFinalsFormat() {
		if (this.isLeagueFinal) {
			this.set('finals_format', 0);
		}

		return this;
	}

	getNewFinalStartRound(rounds, selected_round, start_round_id) {
		const first_id = _.first(rounds).id;
		const last_id = _.last(rounds).id;
		const is_in_range = _.inRange(selected_round, first_id - 1, last_id + 1);
		return is_in_range && selected_round > start_round_id ? selected_round : last_id;
	}
	isNeedToChangeFinalsStartRound(is_finals, play_times, type) {


		return is_finals && play_times === 'flexible' && type === 'head_to_head';
	}

	recalculateFinalsStartRoundFormat(rounds, field_name) {
		const {
			finals_format, type, play_times, finals_start_round, start_round
		} = this.attributes;
		let finals_start_round_actual = finals_start_round
		if(finals_start_round === 'Please select'){
			finals_start_round_actual = 0;
		}
		const final_round_id = parseInt(finals_start_round_actual, 10);
		const start_round_id = parseInt(start_round, 10);
		const number_of_final_rounds = FINAL_ROUNDS[finals_format];

		const form_names = ['final_format', 'start_round'];
		const is_need_to_change_selected_final = form_names.includes(field_name);
		const is_finals = finals_format !== '0' && number_of_final_rounds;

		if (this.isNeedToChangeFinalsStartRound(is_finals, play_times, type)) {
			const final_rounds = rounds.slice(0).filter(({ id }) => id > start_round);
			const round_to_splice = -FINAL_ROUNDS[finals_format] + 1;

			if (round_to_splice) {
				final_rounds.splice(round_to_splice);
			}

			// Default round should be 24 round - finals round. Not the last one available;
			const default_round = final_rounds[final_rounds.length - 2].id;
			const selected_final_round = is_need_to_change_selected_final ? (
				this.getNewFinalStartRound(final_rounds, final_round_id, start_round_id)
			) : final_round_id;
			this.set({
				finals_start_rounds: final_rounds,
				finals_start_round_enabled: true,
				finals_start_round: final_round_id !== 0 ?
					Number(selected_final_round) : Number(default_round)
			});
		}
		else {
			this.set({
				finals_start_rounds: [{ id: 'Please select' }],
				finals_start_round_enabled: false,
				finals_start_round: 0
			});
		}

		return this;
	}

	/**
	 * Check form error in model
	 * @returns {boolean}
	 */
	isFormErrors() {
		return !_.isEmpty(this.get('errors'));
	}

	/**
	 * Check is league started
	 * @returns {LeagueModel}
	 */
	isStarted() {
		let type = this.get('type'),
			status = this.get('status'),
			num_teams = this.get('num_teams'),
			size = this.get('size');

		if (
			status!=='scheduled' ||
			(type==='head_to_head' && size===num_teams)
		) {
			this.set('started', true);
		}

		return this;
	}

	/**
	 * Update form data by form fields
	 * @param rounds
	 * @param onlyFuture
	 * @param field_name - Form changed field name
	 * @returns {LeagueModel}
	 */
	updateFormData(rounds, onlyFuture = true, field_name = '', excludeFinals = false) {
		let new_rounds = rounds.recalculateRounds(
			this.isOnByeRounds, onlyFuture, {}, excludeFinals
		);
		if (field_name === 'league_size') {
			this.checkFinalsFormat();
		}

		this.set('rounds', new_rounds);
		this.recalculateFinalsFormat();
		this.recalculateFinalsStartRoundFormat(new_rounds, field_name);

		return this;
	}

	setRounds(rounds, onlyFuture = true) {
		let new_rounds = rounds.recalculateRounds(this.isOnByeRounds, onlyFuture);

		this.set('rounds', new_rounds);

		return this;
	}

	checkFinalsFormat() {
		const {
			finals_format,
			size
		} = this.attributes;
		const finals_format_int = parseInt(finals_format, 10);

		if (size < 8 && finals_format_int && finals_format_int > 6) {
			this.set({
				finals_format: '6'
			});
		}
	}

	updateFinalsRounds() {
		const {
			finals_format,
			end_round,
			rounds
		} = this.attributes;

		let final_labels_key = 0,
			new_rounds = rounds.filter(round => {
				if( round.id <= end_round ) {

					if(this.isFinalRound(round.id)) {
						round.final_prefix = FINAL_ROUNDS_LABELS[finals_format][final_labels_key];
						final_labels_key++;
					}

					return round;
				}
			});

		this.set('rounds', new_rounds);

		return this;
	}

	/**
	 * Set Fixture Rounds
	 * @param rounds
	 * @returns {LeagueModel}
	 */
	getFixtureRounds({ rounds }) {
		const start_round = this.get('start_round'),
			end_round = this.get('end_round'),
			is_like_array = true,
			new_rounds = rounds.recalculateRounds(this.isOnByeRounds, false, {
				start_round, end_round, is_like_array
			});

		this.set({
			rounds: new_rounds
		});

		return this;
	}


	setInvitesUrl() {
		this.url = 'leagues_classic/invite_to';
		return this;
	}

	/**
	 *
	 * @param {object} data
	 * @returns {LeagueModel}
	 */
	sendInvites(data = []) {
		this.setInvitesUrl()
			.postData({
				data,
				type: 'league:invites'
			});

		return this;
	}

	setJoinUrl() {
		this.url = 'leagues_classic/join_to';
		return this;
	}

	/**
	 * Join to league by code
	 * @param {string} code
	 */
	join(code) {
		let data = {
			data: {
				code
			},
			type: 'league:join'
		};

		this.setJoinUrl()
			.getData(data);
	}

	clearFilters() {
		this.set({
			filter: '',
			skill_level: ''
		});

		return this;
	}

	filterBy(key, val) {
		this.set(key, val);
	}

	checkAccessToLeague(user_id) {
		let commissioner_id = this.get('commissioner'),
			is_commissioner = user_id === commissioner_id;

		this.set('is_commissioner', is_commissioner);

		return this;
	}

	/**
	 * Check is possible get out of this league
	 * @returns {LeagueModel}
	 */
	checkIsPossibleLeave(user_id) {
		let type_class = this.get('class'),
			team_rank = this.get('team_rank'),
			league_type = this.get('type'),
			is_club = type_class==='club',
			is_possible_leave = !is_club && (team_rank === 0 || league_type === 'open');

		let is_leave_commissioner = is_possible_leave && this.get('commissioner') !== user_id;
	
		this.set('is_possible_leave', is_possible_leave);
		this.set('is_leave_commissioner', is_leave_commissioner);
		this.set('is_club', is_club);

		return this;
	}

	checkLeagueMember() {
		return  this.get('team_id') ;
	}

	setAvatar() {
		let avatar_version = this.get( 'avatar_version' ),
			is_club        = this.get( 'is_club' );

		if ( avatar_version || is_club ) {
			let avatar = leagues_avatars_path + this.get( 'id' ) + '.png?v=' + avatar_version;
			this.set( 'avatar', avatar );
		}

		return this;
	}

	updateUserName() {
		let last_name = this.get('last_name').charAt(0);
		this.set('last_name', last_name);

		return this;
	}

	checkOriginTypeLeague(check_origin_league_type) {
		if (!check_origin_league_type) {
			let type = this.get('type');
			this.set('origin_type', type);
		}

		return this;
	}

	setInviteAllUrl() {
		this.url = 'leagues_classic/regenerate_invite';
		return this;
	}

	inviteAll(data = {}) {
		this.setInviteAllUrl()
			.postData({
				data,
				type: 'league:invites'
			});

		return this;
	}

	setRemoveUrl() {
		this.url = 'leagues_classic/remove';
		return this;
	}

	remove() {
		this.setRemoveUrl()
			.getData({
				type: 'league:remove',
				data: {
					id: this.get('id')
				},
			});
	}

	checkAccessToSettings() {
		let is_club = this.get('is_club'),
			started = this.get('started'),
			is_commissioner = this.get('is_commissioner'),
			privacy = this.get('privacy'),
			is_access = false;

		if (!is_club && !started) {
			if (!is_commissioner && !privacy) {
				is_access = false;
			}
			else {
				is_access = true;
			}
		}

		this.set('if_settings_access', is_access);

		return this;
	}

	setTeamProjScore( team, round_id ) {
		if(_.isEmpty(team)) {
			return {};
		}

		const
			rounds = this.get( 'rounds_collection' ),
			round = rounds.get(round_id),
			is_complete = round.is_complete;
		const
			team_players = this.get( 'players' ) ? this.getTeamPlayers( team, round_id ) : {},
			is_playing_now = this.checkIsPlayingNowTeam(team_players);

		if( User.has_assistant_coach && this.get( 'players' ) && !is_complete ) {
			const
				id = _.result(round, 'attributes.id', 0),
				proj_leader_score_key = `stats.proj_scores.${id}`;

			let proj_leader_id = this.getProjLeaderId({
				team,
				proj_leader_score_key
			});
			let proj_score = 0;


			if (team_players[proj_leader_id]) {
				let stats = team_players[proj_leader_id];
				proj_score = _.result(stats, proj_leader_score_key, 0);

			}

			proj_score += team.getProjScore({
				players_by_id: { ...team_players },
				is_active_round: round.is_active,
				is_matches_completed: round.is_matches_completed
			});

			team.set( { proj_score, team_players } );
		}


		team.set({ is_playing_now }, { silent: true });

		return team;
	}

	getTeamPlayers(team, current_round) {
		if(_.isEmpty(team)) {
			return {};
		}

		const
			{ captain, vice_captain } = team.get('lineup'),
			players = this.get('players'),
			captain_player = players.get(captain),
			rounds = this.get( 'rounds_collection' ),
			selected_round = this.get( 'rounds_collection' ).get( current_round ),
			round = selected_round.toJSON();
		const
			is_cap_scored = captain && captain_player.isPlayerScored({ round }),
			is_cap_played = captain && captain_player.alreadyPlayed({ round }),
			is_preseason = rounds.is_preseason;

		let leader_id = is_cap_scored ? captain : vice_captain;

		if (!is_cap_played) {
			leader_id = captain;
		}
		return _.keyBy(team.team_players_id.map(function(id) {
			const
				player_obj = players.get(id) ? players.get(id).toJSON() : {},
				player = new Player( { ...player_obj } );
			return player.setStatsPointsLastRound({
				is_preseason, rounds: rounds.toJSON(),
				round_id: round.id-1,
				leader_id: !selected_round.is_scheduled ? leader_id : null
			}).toJSON();
		}), 'id');
	}

	checkIsPlayingNowTeam(team_players) {
		const playing = _.find( team_players,
			player => {
				return player.is_playing_now;
			}
		);

		return !!playing;
	}

	getProjLeaderId({ team, proj_leader_score_key }) {
		const { captain, vice_captain } = team.get('lineup');
		let proj_leader_id = captain || vice_captain;
		if (proj_leader_id) {
			const
				proj_leader = this.get( 'players' ).get(proj_leader_id),
				proj_leader_score = _.result(proj_leader.toJSON(), proj_leader_score_key, 0);

			if (!proj_leader_score) {
				proj_leader_id = vice_captain;
			}
		}

		return proj_leader_id;

	}

	setDefaultData() {
		this.set({
			privacy: 0,
			size: 16,
			start_round: 2,
			bye_rounds: 0,
			finals_format: 8,
			finals_start_round: 23,
			play_times: 'flexible',
			coaches_box: 1,
			advanced_options: 'basic'
		});
	}
}